import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../constants/providers/AuthProvider';
import { NotificationProvider, useNotificationCenter } from '../../context/NotificationContext';
import { NotificationCenter } from '../notifications/NotificationCenter';
import { MobileAppBanner } from '../ui/MobileAppBanner';
import { AddPaymentDrawer } from '../billing/AddPaymentDrawer';
import { AddStudentModal } from '../admin/AddStudentModal';
import { TopBarV2 } from './TopBarV2';
import { SidebarV2 } from './SidebarV2';
import { PageLayout } from './PageLayout';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { ROUTES } from '../../constants/routes';
import { hasIncompleteOnboarding, getOnboardingUrl, getCurrentOnboardingSection } from '../../utils/onboardingCheck';

const SIDEBAR_STORAGE_KEY = 'rollops-sidebar-v2-open';

/**
 * Inner content component that consumes the NotificationContext.
 *
 * Mirrors the auth checks and onboarding redirects from the original
 * AuthenticatedLayoutContent, but uses the new TopBarV2 + SidebarV2 shell
 * instead of the legacy TopBar + UserNavHost.
 */
function AuthenticatedLayoutV2Content({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, loading: authLoading } = useAuth();
  const { data: companyId } = useCurrentCompany();
  const { isOpen: isNotificationOpen, closeNotificationCenter } = useNotificationCenter();

  const [showAddPaymentDrawer, setShowAddPaymentDrawer] = useState(false);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);

  // Sidebar toggle state (persisted to localStorage)
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    const stored = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    return false; // Default collapsed
  });

  const toggleSidebar = () => {
    setSidebarOpen((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
      }
      return next;
    });
  };

  // Handle onboarding redirects (carried over from original layout)
  useEffect(() => {
    if (authLoading || !session) return;

    const isOnboardingPage = location.pathname.startsWith('/onboarding');
    const isSubscriptionRelatedPage =
      ['/subscriptions-plans', '/subscriptions-plans/rollops', '/subscriptions-plans/gym', '/subscriptions', '/discount-codes', '/billing/discount-codes'].includes(location.pathname) ||
      location.pathname.startsWith('/subscriptions-plans');
    const currentOnboardingSection = getCurrentOnboardingSection();
    const isInSubscriptionsStep = currentOnboardingSection === 'subscriptions';

    if (!isOnboardingPage && !(isSubscriptionRelatedPage && isInSubscriptionsStep) && hasIncompleteOnboarding()) {
      const onboardingUrl = getOnboardingUrl();
      navigate(onboardingUrl);
    }
  }, [session, authLoading, navigate, location.pathname]);

  if (authLoading) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#111010] text-[#EBFFEE] font-['Inter']">
      {/* Top Bar - Always visible (sidebar overlays its left edge on desktop) */}
      <TopBarV2
        onAddPayment={() => setShowAddPaymentDrawer(true)}
        onAddStudent={() => (companyId ? setShowAddStudentModal(true) : navigate(ROUTES.STUDENTS))}
        onAddQR={() => navigate(ROUTES.QR_CODES)}
      />

      {/* Sidebar - Full height, toggle inside, overlays page and header when expanded */}
      <SidebarV2
        isOpen={sidebarOpen}
        onToggle={toggleSidebar}
        onAddPayment={() => setShowAddPaymentDrawer(true)}
        onAddStudent={() => (companyId ? setShowAddStudentModal(true) : navigate(ROUTES.STUDENTS))}
      />

      {/* Main Content Area - Content stays fixed; expanded sidebar overlays on top */}
      {/* Lateral spacing is controlled here (navbar/layout), not in PageLayout */}
      <div className="flex-1 flex min-h-0 pt-16">
        {/* No left margin on mobile (sidebar hidden), ml-20 on md+ for collapsed rail only */}
        <div className="flex-1 min-w-0 min-h-0 flex flex-col pb-16 md:pb-0 ml-0 md:ml-20 px-4 sm:px-5 md:px-6 lg:px-8">
          <main className="flex-1 flex flex-col min-h-0 overflow-auto w-full min-w-0">
            <PageLayout maxWidth="full" padding="none">
              {children}
            </PageLayout>
          </main>

          {/* Mobile App Banner */}
          <MobileAppBanner />
        </div>
      </div>

      {/* Notification Center Modal */}
      <NotificationCenter
        isOpen={isNotificationOpen}
        onClose={closeNotificationCenter}
      />

      {/* Add Payment Drawer - opened from sidebar hot button */}
      <AddPaymentDrawer
        isOpen={showAddPaymentDrawer}
        onClose={() => setShowAddPaymentDrawer(false)}
      />

      {/* Add Student Modal - opened from sidebar hot button */}
      {companyId && (
        <AddStudentModal
          isOpen={showAddStudentModal}
          onClose={() => setShowAddStudentModal(false)}
          companyId={companyId}
        />
      )}
    </div>
  );
}

/**
 * AuthenticatedLayoutV2 - New layout shell for the redesigned UI.
 *
 * Drop-in replacement for AuthenticatedLayout. Wraps pages with:
 * - TopBarV2  (always-visible header with company switcher)
 * - SidebarV2 (hover-to-expand icon rail on desktop, bottom nav on mobile)
 * - NotificationProvider & NotificationCenter
 *
 * Used by setting `layout: 'authenticated-v2'` in routeDefinitions.ts.
 * Routes can be migrated one at a time from 'authenticated' to 'authenticated-v2'.
 */
export function AuthenticatedLayoutV2() {
  const { session } = useAuth();

  // Redirect to sign-in if not authenticated
  if (!session) {
    return <Navigate to="/signin" replace />;
  }

  return (
    <NotificationProvider>
      <AuthenticatedLayoutV2Content>
        <Outlet />
      </AuthenticatedLayoutV2Content>
    </NotificationProvider>
  );
}
